/**
 * Angular Material Palette Generator
 *
 * Generates complete Material Design color palettes from base colors
 * Includes shades from 50 to 900, accent shades (A100-A700), and contrast colors
 */

import { hexToRgb, rgbToHsl, hslToRgb, rgbToHex, adjustLightness } from "@nexu-library/utilities";

export interface MaterialPalette {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  A100: string;
  A200: string;
  A400: string;
  A700: string;
  contrast: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    A100: string;
    A200: string;
    A400: string;
    A700: string;
  };
}

/**
 * Calculate the relative luminance of a color
 * Used to determine if text should be light or dark
 */
function getLuminance(hex: string): number {
  const rgb = hexToRgb(hex);

  // Convert RGB to linear RGB
  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map((val) => {
    const channel = val / 255;
    return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
  });

  // Calculate relative luminance
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Determine if a color should have light or dark text (contrast)
 * Returns white (#ffffff) for dark backgrounds, black (#000000) for light backgrounds
 */
function getContrastColor(hex: string): string {
  const luminance = getLuminance(hex);
  // W3C WCAG 2.0 recommendation: use white text if luminance < 0.5
  return luminance > 0.5 ? "#000000" : "#ffffff";
}

/**
 * Generate a complete Material Design palette from a base color
 * Base color should typically be the 500 shade
 *
 * Material Design shades reference:
 * - 50: Lightest (almost white with a hint of color)
 * - 100-400: Light shades
 * - 500: Base color (main)
 * - 600-900: Dark shades
 * - A100-A700: Accent shades (more saturated)
 */
export function generateMaterialPalette(baseHex: string): MaterialPalette {
  const rgb = hexToRgb(baseHex);
  const hsl = rgbToHsl(rgb);
  const baseLightness = hsl.l;

  // Generate main palette (50-900)
  const palette: Partial<MaterialPalette> = {
    50: adjustLightness(baseHex, 95 - baseLightness), // Almost white
    100: adjustLightness(baseHex, 90 - baseLightness), // Very light
    200: adjustLightness(baseHex, 80 - baseLightness), // Light
    300: adjustLightness(baseHex, 70 - baseLightness), // Light-medium
    400: adjustLightness(baseHex, 60 - baseLightness), // Medium-light
    500: baseHex, // Base color
    600: adjustLightness(baseHex, 45 - baseLightness), // Medium-dark
    700: adjustLightness(baseHex, 35 - baseLightness), // Dark
    800: adjustLightness(baseHex, 25 - baseLightness), // Very dark
    900: adjustLightness(baseHex, 15 - baseLightness), // Darkest
  };

  // Generate accent shades (more saturated versions)
  // Accent shades are typically brighter and more vibrant
  const saturatedHsl = { ...hsl };
  saturatedHsl.s = Math.min(100, hsl.s + 15); // Increase saturation by 15%
  const saturatedBase = rgbToHex(hslToRgb(saturatedHsl));

  palette.A100 = adjustLightness(saturatedBase, 85 - baseLightness);
  palette.A200 = adjustLightness(saturatedBase, 70 - baseLightness);
  palette.A400 = adjustLightness(saturatedBase, 55 - baseLightness);
  palette.A700 = adjustLightness(saturatedBase, 40 - baseLightness);

  // Generate contrast colors for each shade
  const contrast: MaterialPalette["contrast"] = {
    50: getContrastColor(palette[50]!),
    100: getContrastColor(palette[100]!),
    200: getContrastColor(palette[200]!),
    300: getContrastColor(palette[300]!),
    400: getContrastColor(palette[400]!),
    500: getContrastColor(palette[500]!),
    600: getContrastColor(palette[600]!),
    700: getContrastColor(palette[700]!),
    800: getContrastColor(palette[800]!),
    900: getContrastColor(palette[900]!),
    A100: getContrastColor(palette.A100!),
    A200: getContrastColor(palette.A200!),
    A400: getContrastColor(palette.A400!),
    A700: getContrastColor(palette.A700!),
  };

  return {
    ...palette,
    contrast,
  } as MaterialPalette;
}

/**
 * Generate Material palettes for primary, accent, and warn colors
 */
export interface MaterialThemePalettes {
  primary: MaterialPalette;
  accent: MaterialPalette;
  warn: MaterialPalette;
}

/**
 * Generate all Material Design palettes from theme colors
 *
 * @param theme Theme object with neutral, primary, and secondary colors
 * @returns Object with primary, accent, and warn Material palettes
 */
export function generateMaterialThemePalettes(theme: {
  neutral: string;
  primary: string;
  secondary: string;
}): MaterialThemePalettes {
  return {
    primary: generateMaterialPalette(theme.primary),
    accent: generateMaterialPalette(theme.secondary),
    warn: generateMaterialPalette("#f44336"), // Default Material red for warn
  };
}

/**
 * Apply Material Design palettes as CSS variables
 * This allows Angular Material components to use the dynamic colors
 */
export function applyMaterialPalettesAsCSSVariables(palettes: MaterialThemePalettes): void {
  const root = document.documentElement;

  // Apply primary palette
  Object.entries(palettes.primary).forEach(([shade, value]) => {
    if (shade !== "contrast") {
      root.style.setProperty(`--mat-primary-${shade}`, value);
    }
  });

  // Apply primary contrast colors
  Object.entries(palettes.primary.contrast).forEach(([shade, value]) => {
    root.style.setProperty(`--mat-primary-contrast-${shade}`, value);
  });

  // Apply accent palette
  Object.entries(palettes.accent).forEach(([shade, value]) => {
    if (shade !== "contrast") {
      root.style.setProperty(`--mat-accent-${shade}`, value);
    }
  });

  // Apply accent contrast colors
  Object.entries(palettes.accent.contrast).forEach(([shade, value]) => {
    root.style.setProperty(`--mat-accent-contrast-${shade}`, value);
  });

  // Apply warn palette
  Object.entries(palettes.warn).forEach(([shade, value]) => {
    if (shade !== "contrast") {
      root.style.setProperty(`--mat-warn-${shade}`, value);
    }
  });

  // Apply warn contrast colors
  Object.entries(palettes.warn.contrast).forEach(([shade, value]) => {
    root.style.setProperty(`--mat-warn-contrast-${shade}`, value);
  });

  // Apply commonly used Material variables
  root.style.setProperty("--primary", palettes.primary[500]);
  root.style.setProperty("--primary-lighter", palettes.primary[300]);
  root.style.setProperty("--primary-darker", palettes.primary[700]);
  root.style.setProperty("--primary-contrast", palettes.primary.contrast[500]);

  root.style.setProperty("--accent", palettes.accent[500]);
  root.style.setProperty("--accent-lighter", palettes.accent[300]);
  root.style.setProperty("--accent-darker", palettes.accent[700]);
  root.style.setProperty("--accent-contrast", palettes.accent.contrast[500]);

  root.style.setProperty("--warn", palettes.warn[500]);
  root.style.setProperty("--warn-contrast", palettes.warn.contrast[500]);
}

/**
 * Export Material palette as a JavaScript object (for debugging)
 */
export function exportMaterialPaletteForDebugging(palettes: MaterialThemePalettes): Record<string, any> {
  return {
    primary: palettes.primary,
    accent: palettes.accent,
    warn: palettes.warn,
  };
}
